import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { InstructionRSJIncomeType } from '../../../../../shared/models/enums/Instruction-rsj-income-type.enum';
import { RsjInstructionIncomeDto } from '../../dto/rsj-instruction-income.dto';
import { RsjInstructionResourcesDto } from '../../dto/rsj-instruction-resources.dto';

@Component({
  selector: 'app-rsj-instruction-resources',
  templateUrl: './rsj-instruction-resources.component.html',
  standalone: false,
})
export class RsjInstructionResourcesComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject();

  public resourcesForm: UntypedFormGroup;
  public updated: boolean = false;
  public submitted: boolean = false;

  @Input() public rsjResources: RsjInstructionResourcesDto;
  @Input() public disabled: boolean;

  constructor(private readonly formBuilder: UntypedFormBuilder) {}

  public ngOnInit(): void {
    this.initForm();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit(): boolean {
    if (this.updated) {
      this.submitted = true;
    }
    return !this.updated || this.resourcesForm.valid;
  }

  public getDto(): RsjInstructionResourcesDto {
    if (this.resourcesForm.valid) {
      const incomes = [
        new RsjInstructionIncomeDto(
          this.resourcesForm.controls.professionnalIncomeId.value,
          InstructionRSJIncomeType.Professional,
          this.resourcesForm.controls.professionnalIncomePrev.value,
          this.resourcesForm.controls.professionnalIncomeCurr.value,
          this.resourcesForm.controls.professionnalIncomeNext.value,
          this.resourcesForm.controls.professionnalIncomeNextTwo.value,
          this.resourcesForm.controls.professionnalIncomeNextThree.value,
        ),
        new RsjInstructionIncomeDto(
          this.resourcesForm.controls.replacementIncomeId.value,
          InstructionRSJIncomeType.Replacement,
          this.resourcesForm.controls.replacementIncomePrev.value,
          this.resourcesForm.controls.replacementIncomeCurr.value,
          this.resourcesForm.controls.replacementIncomeNext.value,
          this.resourcesForm.controls.replacementIncomeNextTwo.value,
          this.resourcesForm.controls.replacementIncomeNextThree.value,
        ),
        new RsjInstructionIncomeDto(
          this.resourcesForm.controls.trainingIncomeId.value,
          InstructionRSJIncomeType.Training,
          this.resourcesForm.controls.trainingIncomePrev.value,
          this.resourcesForm.controls.trainingIncomeCurr.value,
          this.resourcesForm.controls.trainingIncomeNext.value,
          this.resourcesForm.controls.trainingIncomeNextTwo.value,
          this.resourcesForm.controls.trainingIncomeNextThree.value,
        ),
        new RsjInstructionIncomeDto(
          this.resourcesForm.controls.socialIncomeId.value,
          InstructionRSJIncomeType.SocialSupport,
          this.resourcesForm.controls.socialIncomePrev.value,
          this.resourcesForm.controls.socialIncomeCurr.value,
          this.resourcesForm.controls.socialIncomeNext.value,
          this.resourcesForm.controls.socialIncomeNextTwo.value,
          this.resourcesForm.controls.socialIncomeNextThree.value,
        ),
      ];
      if (
        ![null, ''].includes(this.resourcesForm.controls.otherIncomePrev.value) ||
        ![null, ''].includes(this.resourcesForm.controls.otherIncomeCurr.value) ||
        ![null, ''].includes(this.resourcesForm.controls.otherIncomeNext.value) ||
        ![null, ''].includes(this.resourcesForm.controls.otherIncomeNextTwo.value) ||
        ![null, ''].includes(this.resourcesForm.controls.otherIncomeNextThree.value)
      ) {
        incomes.push(
          new RsjInstructionIncomeDto(
            this.resourcesForm.controls.otherIncomeId.value,
            InstructionRSJIncomeType.Other,
            this.resourcesForm.controls.otherIncomePrev.value,
            this.resourcesForm.controls.otherIncomeCurr.value,
            this.resourcesForm.controls.otherIncomeNext.value,
            this.resourcesForm.controls.otherIncomeNextTwo.value,
            this.resourcesForm.controls.otherIncomeNextThree.value,
          ),
        );
      }
      this.rsjResources = {
        incomes,
        otherIncomePrecision: this.resourcesForm.controls.otherIncomePrecision.value,
        withoutSolidarityPlan: this.resourcesForm.controls.withoutSolidarityPlan.value ?? false,
        withoutYouthPlan: this.resourcesForm.controls.withoutYouthPlan.value ?? false,
      } as RsjInstructionResourcesDto;
    }
    return this.rsjResources;
  }

  private initForm(): void {
    let professionnalIncome: RsjInstructionIncomeDto =
      (this.rsjResources?.incomes ?? []).find((item) => item.type === InstructionRSJIncomeType.Professional) ?? new RsjInstructionIncomeDto();
    let replacementIncome: RsjInstructionIncomeDto =
      (this.rsjResources?.incomes ?? []).find((item) => item.type === InstructionRSJIncomeType.Replacement) ?? new RsjInstructionIncomeDto();
    let socialSupportIncome: RsjInstructionIncomeDto =
      (this.rsjResources?.incomes ?? []).find((item) => item.type === InstructionRSJIncomeType.SocialSupport) ?? new RsjInstructionIncomeDto();
    let trainingIncome: RsjInstructionIncomeDto =
      (this.rsjResources?.incomes ?? []).find((item) => item.type === InstructionRSJIncomeType.Training) ?? new RsjInstructionIncomeDto();
    let otherIncome: RsjInstructionIncomeDto =
      (this.rsjResources?.incomes ?? []).find((item) => item.type === InstructionRSJIncomeType.Other) ?? new RsjInstructionIncomeDto();
    this.resourcesForm = this.formBuilder.group(
      {
        professionnalIncomeId: [professionnalIncome.id],
        professionnalIncomePrev: [professionnalIncome.previousMonth, [Validators.required]],
        professionnalIncomeCurr: [professionnalIncome.currentMonth, [Validators.required]],
        professionnalIncomeNext: [professionnalIncome.nextMonth, [Validators.required]],
        professionnalIncomeNextTwo: [professionnalIncome.nextTwoMonth, [Validators.required]],
        professionnalIncomeNextThree: [professionnalIncome.nextThreeMonth, [Validators.required]],
        replacementIncomeId: [replacementIncome.id],
        replacementIncomePrev: [replacementIncome.previousMonth, [Validators.required]],
        replacementIncomeCurr: [replacementIncome.currentMonth, [Validators.required]],
        replacementIncomeNext: [replacementIncome.nextMonth, [Validators.required]],
        replacementIncomeNextTwo: [replacementIncome.nextTwoMonth, [Validators.required]],
        replacementIncomeNextThree: [replacementIncome.nextThreeMonth, [Validators.required]],
        trainingIncomeId: [trainingIncome.id],
        trainingIncomePrev: [trainingIncome.previousMonth, [Validators.required]],
        trainingIncomeCurr: [trainingIncome.currentMonth, [Validators.required]],
        trainingIncomeNext: [trainingIncome.nextMonth, [Validators.required]],
        trainingIncomeNextTwo: [trainingIncome.nextTwoMonth, [Validators.required]],
        trainingIncomeNextThree: [trainingIncome.nextThreeMonth, [Validators.required]],
        socialIncomeId: [socialSupportIncome.id],
        socialIncomePrev: [socialSupportIncome.previousMonth, [Validators.required]],
        socialIncomeCurr: [socialSupportIncome.currentMonth, [Validators.required]],
        socialIncomeNext: [socialSupportIncome.nextMonth, [Validators.required]],
        socialIncomeNextTwo: [socialSupportIncome.nextTwoMonth, [Validators.required]],
        socialIncomeNextThree: [socialSupportIncome.nextThreeMonth, [Validators.required]],
        otherIncomePrecision: [this.rsjResources?.otherIncomePrecision],
        otherIncomeId: [otherIncome.id],
        otherIncomePrev: [otherIncome.previousMonth],
        otherIncomeCurr: [otherIncome.currentMonth],
        otherIncomeNext: [otherIncome.nextMonth],
        otherIncomeNextTwo: [otherIncome.nextTwoMonth],
        otherIncomeNextThree: [otherIncome.nextThreeMonth],
        withoutSolidarityPlan: [this.rsjResources?.withoutSolidarityPlan, Validators.requiredTrue],
        withoutYouthPlan: [this.rsjResources?.withoutYouthPlan, Validators.requiredTrue],
      },
      { validator: this.otherIncomeRequiredValidator() },
    );
    this.resourcesForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => (this.updated = true));
    if (this.disabled) {
      this.resourcesForm.disable({ emitEvent: false });
    }
  }

  public getPreviousMonthTotal(): number {
    return (
      +this.resourcesForm.controls.professionnalIncomePrev.value +
      +this.resourcesForm.controls.replacementIncomePrev.value +
      +this.resourcesForm.controls.trainingIncomePrev.value +
      +this.resourcesForm.controls.socialIncomePrev.value +
      +this.resourcesForm.controls.otherIncomePrev.value
    );
  }

  public getCurrentMonthTotal(): number {
    return (
      +this.resourcesForm.controls.professionnalIncomeCurr.value +
      +this.resourcesForm.controls.replacementIncomeCurr.value +
      +this.resourcesForm.controls.trainingIncomeCurr.value +
      +this.resourcesForm.controls.socialIncomeCurr.value +
      +this.resourcesForm.controls.otherIncomeCurr.value
    );
  }

  public getNextMonthTotal(): number {
    return (
      +this.resourcesForm.controls.professionnalIncomeNext.value +
      +this.resourcesForm.controls.replacementIncomeNext.value +
      +this.resourcesForm.controls.trainingIncomeNext.value +
      +this.resourcesForm.controls.socialIncomeNext.value +
      +this.resourcesForm.controls.otherIncomeNext.value
    );
  }

  public getNextTwoMonthTotal(): number {
    return (
      +this.resourcesForm.controls.professionnalIncomeNextTwo.value +
      +this.resourcesForm.controls.replacementIncomeNextTwo.value +
      +this.resourcesForm.controls.trainingIncomeNextTwo.value +
      +this.resourcesForm.controls.socialIncomeNextTwo.value +
      +this.resourcesForm.controls.otherIncomeNextTwo.value
    );
  }

  public getNextThreeMonthTotal(): number {
    return (
      +this.resourcesForm.controls.professionnalIncomeNextThree.value +
      +this.resourcesForm.controls.replacementIncomeNextThree.value +
      +this.resourcesForm.controls.trainingIncomeNextThree.value +
      +this.resourcesForm.controls.socialIncomeNextThree.value +
      +this.resourcesForm.controls.otherIncomeNextThree.value
    );
  }

  public incomesHaveRequiredError(): boolean {
    return this.mainIncomeControls.some((control) => control.errors?.required) || this.resourcesForm.errors?.otherIncomeRequired === true;
  }

  public fillIncomes(): void {
    this.mainIncomeControls.forEach((control) => {
      if (!control.value) {
        control.setValue(0);
      }
    });
  }

  private get mainIncomeControls(): AbstractControl[] {
    return [
      this.resourcesForm.controls.professionnalIncomePrev,
      this.resourcesForm.controls.professionnalIncomeCurr,
      this.resourcesForm.controls.professionnalIncomeNext,
      this.resourcesForm.controls.professionnalIncomeNextTwo,
      this.resourcesForm.controls.professionnalIncomeNextThree,
      this.resourcesForm.controls.replacementIncomePrev,
      this.resourcesForm.controls.replacementIncomeCurr,
      this.resourcesForm.controls.replacementIncomeNext,
      this.resourcesForm.controls.replacementIncomeNextTwo,
      this.resourcesForm.controls.replacementIncomeNextThree,
      this.resourcesForm.controls.trainingIncomePrev,
      this.resourcesForm.controls.trainingIncomeCurr,
      this.resourcesForm.controls.trainingIncomeNext,
      this.resourcesForm.controls.trainingIncomeNextTwo,
      this.resourcesForm.controls.trainingIncomeNextThree,
      this.resourcesForm.controls.socialIncomePrev,
      this.resourcesForm.controls.socialIncomeCurr,
      this.resourcesForm.controls.socialIncomeNext,
      this.resourcesForm.controls.socialIncomeNextTwo,
      this.resourcesForm.controls.socialIncomeNextThree,
    ];
  }

  private otherIncomeRequiredValidator(): ValidatorFn {
    return (form: UntypedFormGroup): ValidationErrors | null => {
      const otherIncomesControls: AbstractControl[] = [
        form.controls.otherIncomePrev,
        form.controls.otherIncomeCurr,
        form.controls.otherIncomeNext,
        form.controls.otherIncomeNextTwo,
        form.controls.otherIncomeNextThree,
      ];
      return otherIncomesControls.some((control) => [null, ''].includes(control.value)) &&
        otherIncomesControls.some((control) => ![null, ''].includes(control.value))
        ? { otherIncomeRequired: true }
        : null;
    };
  }
}
